import { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { cn, cleanText } from "@/lib/utils";
import { projectSlug } from "@/lib/slug";
import { trackEvent } from "@/analytics/ga4";

/**
 * WorkCard — an exhibition piece, not a card. The artwork leads full-bleed
 * inside a clip-path reveal; the caption (title + category + arrow) sits
 * BELOW the image, never as a pill overlaid on the artwork (Taste §9.F /
 * Impeccable). Shape (column span + aspect ratio) comes from the parent
 * editorial rhythm so pieces vary in scale. Hover feedback lives on the media
 * frame, not the raw image (gated to fine pointers). Links to /work/:slug.
 */
export const WorkCard = ({ item, shape = {}, idx = 0 }) => {
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.28 });
    const slug = projectSlug(item);
    const feature = shape.kind === "feature" || shape.kind === "wide";
    const title = cleanText(item.title);
    const category = cleanText(item.category);
    const description = cleanText(item.description);

    return (
        <motion.div
            ref={ref}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 26 }}
            animate={inView ? (reduce ? { opacity: 1 } : { opacity: 1, y: 0 }) : undefined}
            transition={{ duration: 0.7, delay: 0.05 * (idx % 5), ease: [0.16, 1, 0.3, 1] }}
            className={cn(feature ? "col-span-2" : "col-span-1", shape.col)}
        >
            <Link
                to={`/work/${slug}`}
                onClick={() => trackEvent("project_view", { category: item.category, item_id: item.id })}
                className="pg-exhibit group"
                data-testid="portfolio-card"
                data-project-id={item.id}
                aria-label={`View project: ${title}`}
            >
                <div
                    className={cn("pg-exhibit__media pg-clip-reveal", shape.ar || "aspect-[16/11]")}
                    data-shown={inView ? "true" : "false"}
                >
                    <img
                        src={item.image}
                        alt={`${title}, ${category}`}
                        loading="lazy"
                        draggable={false}
                        className="pg-exhibit__img"
                    />
                </div>

                <div className="pg-exhibit__caption">
                    <div className="min-w-0">
                        <h3 className={cn("pg-exhibit__title", feature && "pg-exhibit__title--lg")}>
                            {title}
                        </h3>
                        {feature && description && (
                            <p className="pg-exhibit__desc">{description}</p>
                        )}
                    </div>
                    <span className="pg-exhibit__meta-row">
                        <span className="pg-exhibit__meta">{category}</span>
                        <span className="pg-exhibit__arrow" aria-hidden="true">
                            <ArrowUpRight className="h-4 w-4" strokeWidth={1.6} />
                        </span>
                    </span>
                </div>
            </Link>
        </motion.div>
    );
};

export default WorkCard;
